<template>
    <div class="page-cabinet-verification-profile-general">
        <ui-form
            v-bind:model="fields"
            v-bind:validation="$v"
            v-bind:submit-handler="submit"
            v-on:update="updateFormData"
            gap="wide"
        />
    </div>
</template>

<script>
import { mapState } from 'vuex';
import moment from 'moment';
import forms from '~/forms';

const isScreenLarge = () => window.innerWidth >= 768;

export default {
    name: 'page-cabinet-verification-profile-general',
    props: {
        isTouched: {
            type: Boolean,
        },
    },
    data: () => ({
        isLargeScreen: isScreenLarge(),
        observer: null,
    }),
    validations() {
        const result = { fields: {} };
        if (this.isSnilsAndInnNeeded) {
            result.fields.snils = { ...forms.validations.snils };
        }
        if (this.isIP) {
            result.fields.ogrnip = { ...forms.validations.ogrnip };
        }
        if (this.isPerson || this.isIP) {
            result.fields.inn = { ...forms.validations.inn12 };
        }
        const nameValidation = {
            value: {
                isValid: forms.validations.isCyrillic.value.isValid,
                required: forms.validations.genericRequired.value.required,
            },
        };
        result.fields.lastName = nameValidation;
        result.fields.firstName = nameValidation;
        if (!this.fields.noSecondName.value) {
            result.fields.secondName = nameValidation;
        } else {
            result.fields.secondName = {};
        }
        result.fields.phone = { ...forms.validations.phone };
        result.fields.passportSeries = { ...forms.validations.passportSeries };
        result.fields.passportNumber = { ...forms.validations.passportNumber };
        return result;
    },
    computed: {
        ...mapState('users', [
            'getUserProfileResult',
        ]),
        ...mapState('verification/profile', [
            'general',
            'ip',
            'juridical',
        ]),
        isSnilsAndInnNeeded() {
            return this.getUserProfileResult && (this.isPerson || this.isIP);
        },
        isPerson() {
            return this.getUserProfileResult.legalType === 'F';
        },
        isIP() {
            return this.getUserProfileResult.legalType === 'I';
        },
        isJuridical() {
            return this.getUserProfileResult.legalType === 'J';
        },
        fields() {
            let result = {
                lastName: { ...forms.fields.lastName, placeholder: 'Как в паспорте' },
                firstName: { ...forms.fields.firstName, placeholder: 'Как в паспорте' },
                secondName: { ...forms.fields.secondName, placeholder: 'При наличии' },
                noSecondName: { ...forms.fields.noSecondName },
                passportSeries: { ...forms.fields.passportSeries },
                passportNumber: { ...forms.fields.passportNumber },
            };

            let innFieldBase;

            if (this.isJuridical) {
                innFieldBase = forms.fields.inn10dadata;
            } else if (this.isIP) {
                innFieldBase = forms.fields.inn12dadata;
            } else {
                innFieldBase = forms.fields.inn12;
            }

            const isIpDataMissing = !this.ip.ogrnip || !this.ip.ipName || !this.ip.ipAddress;
            const isJuridicalDataMissing =
                !this.juridical.ogrn ||
                !this.juridical.kpp ||
                !this.juridical.organizationName ||
                !this.juridical.organizationAddress ||
                !this.juridical.organizationRegistrationAddress ||
                // !this.juridical.okved ||
                // !this.juridical.organizationNameFull ||
                // !this.juridical.organisationRegistrationDate ||
                !this.juridical.organizationPostalAddress;

            if (
                this.general.inn &&
                (
                    (this.isIP && isIpDataMissing) ||
                    (this.isJuridical && isJuridicalDataMissing)
                )
            ) {
                innFieldBase.isInitialRequestRequired = true;
            }

            if (this.isSnilsAndInnNeeded) {
                result = {
                    ...result,
                    inn: { ...innFieldBase },
                    snils: { ...forms.fields.snils },
                };
            }
            result = {
                ...result,
                phone: { ...forms.fields.phone },
            };

            if (this.isIP) {
                result = {
                    ...result,
                    ogrnip: { ...forms.fields.ogrnip },
                };
            }

            if (this.isLargeScreen) {
                result.phone.isHalf = true;
                result.lastName.isHalf = true;
                result.firstName.isHalf = true;
                result.secondName.isHalf = true;
                if (this.isSnilsAndInnNeeded) {
                    result.inn.isHalf = true;
                    result.snils.isHalf = true;
                }
                result.passportNumber.isHalf = true;
                result.passportSeries.isHalf = true;
            }
            Object.keys(result).forEach(key => {
                if (key === 'ogrnip') {
                    result[key].value = this.ip[key] ?? '';
                } else {
                    result[key].value = this.general[key] === null ? '' : this.general[key];
                }
            });
            return result;
        },
    },
    methods: {
        submit() {
            if (this.$v.$invalid) {
                this.$v.$touch();
                return;
            }
            this.$emit('submit');
        },
        updateFormData(newData) {
            const resultGeneral = {};
            const resultIp = {};
            const resultJuridical = {};

            Object.keys(newData)
                .filter(x => (this.isJuridical ? x !== 'inn' : true))
                .filter(x => (this.isIP ? x !== 'ogrnip' : true))
                .forEach(key => {
                    resultGeneral[key] = newData[key].value === '' ? null : newData[key].value;
                });

            if (!this.isJuridical) {
                if (typeof newData.inn?.value === 'object') {
                    const data = newData.inn.value.data;
                    resultGeneral.inn = data.inn;
                    if (this.isIP) {
                        if (data.fio?.name) {
                            resultGeneral.firstName = data.fio.name;
                        }
                        if (data.fio?.surname) {
                            resultGeneral.lastName = data.fio.surname;
                        }
                        if (data.fio?.patronymic) {
                            resultGeneral.secondName = data.fio.patronymic;
                        }
                        if (data.ogrn) {
                            resultIp.ogrnip = data.ogrn;
                        }
                        // eslint-disable-next-line camelcase
                        if (data.name?.short_with_opf) {
                            // eslint-disable-next-line camelcase
                            resultIp.ipName = data.name.short_with_opf;
                        }
                        // eslint-disable-next-line camelcase
                        if (data.address?.unrestricted_value) {
                            // eslint-disable-next-line camelcase
                            resultIp.ipAddress = data.address.unrestricted_value;
                        }
                    }
                } else {
                    resultGeneral.inn = newData.inn.value;
                }
            }

            if (this.isIP && newData.ogrnip) {
                resultIp['ogrnip'] = newData['ogrnip'].value === '' ? null : newData['ogrnip'].value;
            }

            this.$store.state.verification.profile.general = {
                ...this.$store.state.verification.profile.general,
                ...resultGeneral,
            };
            if (Object.keys(resultIp).length > 0) {
                this.$store.state.verification.profile.ip = {
                    ...this.$store.state.verification.profile.ip,
                    ...resultIp,
                };
            }
            if (Object.keys(resultJuridical).length > 0) {
                this.$store.state.verification.profile.juridical = {
                    ...this.$store.state.verification.profile.juridical,
                    ...resultJuridical,
                };
            }
        },
    },
    mounted() {
        this.observer = new ResizeObserver(() => {
            this.isLargeScreen = isScreenLarge();
        });
        this.observer.observe(document.body);
    },
    beforeDestroy() {
        this.observer.disconnect();
    },
    watch: {
        isTouched: {
            handler(newVal) {
                if (newVal) {
                    this.$v.$touch();
                }
            },
            immediate: true,
        },
        '$v.$dirty': {
            handler(newVal) {
                if (newVal) {
                    this.$emit('touch');
                }
            },
            immediate: true,
        },
        '$v.$invalid': {
            handler(newVal) {
                this.$emit('validation-update', { entityName: 'isGeneralDataValid', value: !newVal });
            },
            immediate: true,
        },
        'fields.secondName.value': {
            handler(newVal, oldVal) {
                if (newVal && !oldVal) {
                    this.$store.state.verification.profile.general = {
                        ...this.$store.state.verification.profile.general,
                        noSecondName: false,
                    };
                }
            },
            immediate: true,
        },
    },
};
</script>

<style scoped lang="less">
@import '~theme';

.page-cabinet-verification-profile-general {
}
</style>
